import { Button, Col, Row, Select } from 'antd';
import classNames from 'classnames';
import { find, toPairs } from 'lodash-es';
import { FormattedMessage, useIntl } from 'react-intl';
import { useEffect, useMemo, useState } from 'react';

import {
  getAdjustedDateRangeValues,
  isDateRangeEqual,
} from '../../common/utils/dateRangeUtils';
import { getSelectableYears, now } from '../../common/utils/dateUtils';
import { useDateRangePresets } from '../../config/dateRangeConstants';
import { cssVariables } from '../../styles/cssVariables';
import { pxToNumber } from '../../utils/cssUtils';
import { SingleDateField } from './SingleDateField';
import { useLayoutElementsStyles } from '../layout/layoutElements.styles';
import { useDateRangePickerStyles } from './DateRangePicker.styles';

const { Option } = Select;

const QUARTERS = [
  { value: 0, label: 'Q1' },
  { value: 1, label: 'Q2' },
  { value: 2, label: 'Q3' },
  { value: 3, label: 'Q4' },
];

function DateRangePresetButton({ name, selected, value, labelId, setValue }) {
  return (
    <Button
      className="width-100p"
      type={selected ? 'primary' : 'default'}
      onClick={() => setValue(name, value)}
    >
      <FormattedMessage id={labelId} />
    </Button>
  );
}

function MonthYearSelector({ form, onSelectionChange, validRangeYearsBack }) {
  const intl = useIntl();
  const { styles } = useDateRangePickerStyles();
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);

  const months = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => ({
        value: i,
        label: intl.formatDate(new Date(2000, i, 1), { month: 'long' }),
      })),
    [intl]
  );

  const years = useMemo(
    () => getSelectableYears(validRangeYearsBack).reverse(),
    [validRangeYearsBack]
  );

  useEffect(() => {
    const values = form.getFieldsValue();
    const { dateStart, dateEnd } = values;

    if (dateStart && dateEnd) {
      const startMoment = now(dateStart);
      const endMoment = now(dateEnd);

      const isMonthRange =
        startMoment.isSame(endMoment, 'month') &&
        startMoment.isSame(startMoment.clone().startOf('month'), 'day') &&
        endMoment.isSame(startMoment.clone().endOf('month'), 'day');

      if (isMonthRange) {
        setSelectedMonth(startMoment.month());
        setSelectedYear(startMoment.year());
      }
    }
    // eslint-disable-next-line
  }, [form.getFieldsValue().dateStart, form.getFieldsValue().dateEnd]);

  const handleMonthChange = month => {
    setSelectedMonth(month);
    if (selectedYear !== null && month !== null) {
      const startDate = now().year(selectedYear).month(month).startOf('month');
      const endDate = now().year(selectedYear).month(month).endOf('month');
      form.setFieldsValue({ dateStart: startDate, dateEnd: endDate });
      onSelectionChange?.();
    }
  };

  const handleYearChange = year => {
    setSelectedYear(year);
    const dateStartVal = form.getFieldValue('dateStart');
    let month = null;

    if (selectedMonth !== null) {
      month = selectedMonth;
    } else if (dateStartVal) {
      month = now(dateStartVal).month();
    }

    if (month == null || year == null) return;

    const startDate = (dateStartVal ? now(dateStartVal) : now())
      .year(year)
      .month(month)
      .startOf('month');
    const endDate = (dateStartVal ? now(dateStartVal) : now())
      .year(year)
      .month(month)
      .endOf('month');

    form.setFieldsValue({ dateStart: startDate, dateEnd: endDate });
    onSelectionChange?.();
  };

  return (
    <Row gutter={8}>
      <Col span={12}>
        <Select
          placeholder={<FormattedMessage id="dateRange.month" />}
          value={selectedMonth}
          onChange={handleMonthChange}
          className={styles.selector}
          size="small"
        >
          {months.map(month => (
            <Option key={month.value} value={month.value}>
              {month.label}
            </Option>
          ))}
        </Select>
      </Col>
      <Col span={12}>
        <Select
          placeholder={<FormattedMessage id="dateRange.year" />}
          value={selectedYear}
          onChange={handleYearChange}
          className={styles.selector}
          size="small"
        >
          {years.map(year => (
            <Option key={year} value={year}>
              {year}
            </Option>
          ))}
        </Select>
      </Col>
    </Row>
  );
}

function QuarterYearSelector({ form, onSelectionChange, validRangeYearsBack }) {
  const { styles } = useDateRangePickerStyles();
  const [selectedQuarter, setSelectedQuarter] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);

  const years = useMemo(
    () => getSelectableYears(validRangeYearsBack).reverse(),
    [validRangeYearsBack]
  );

  useEffect(() => {
    const values = form.getFieldsValue();
    const { dateStart, dateEnd } = values;

    if (dateStart && dateEnd) {
      const startMoment = now(dateStart);
      const endMoment = now(dateEnd);

      const isQuarterRange =
        startMoment.isSame(endMoment, 'quarter') &&
        startMoment.isSame(startMoment.clone().startOf('quarter'), 'day') &&
        endMoment.isSame(endMoment.clone().endOf('quarter'), 'day');

      if (isQuarterRange) {
        setSelectedQuarter(startMoment.quarter() - 1);
        setSelectedYear(startMoment.year());
      }
    }
    // eslint-disable-next-line
  }, [form.getFieldsValue().dateStart, form.getFieldsValue().dateEnd]);

  const handleQuarterChange = quarter => {
    setSelectedQuarter(quarter);
    if (selectedYear !== null && quarter !== null) {
      const startDate = now()
        .year(selectedYear)
        .quarter(quarter + 1)
        .startOf('quarter');
      const endDate = now()
        .year(selectedYear)
        .quarter(quarter + 1)
        .endOf('quarter');
      form.setFieldsValue({ dateStart: startDate, dateEnd: endDate });
      onSelectionChange?.();
    }
  };

  const handleYearChange = year => {
    setSelectedYear(year);
    const dateStartVal = form.getFieldValue('dateStart');

    let quarter = null;
    if (selectedQuarter !== null) {
      quarter = selectedQuarter;
    } else if (dateStartVal) {
      quarter = now(dateStartVal).quarter() - 1;
    }

    if (quarter == null) return;

    const startFrom = dateStartVal ? now(dateStartVal) : now();
    const startDate = startFrom
      .clone()
      .year(year)
      .quarter(quarter + 1)
      .startOf('quarter');
    const endDate = startFrom
      .clone()
      .year(year)
      .quarter(quarter + 1)
      .endOf('quarter');

    form.setFieldsValue({ dateStart: startDate, dateEnd: endDate });
    onSelectionChange?.();
  };

  return (
    <Row gutter={8}>
      <Col span={12}>
        <Select
          aria-label="Quarter"
          placeholder={<FormattedMessage id="dateRange.quarter" />}
          value={selectedQuarter}
          onChange={handleQuarterChange}
          className={styles.selector}
          size="small"
        >
          {QUARTERS.map(quarter => (
            <Option key={quarter.value} value={quarter.value}>
              {quarter.label}
            </Option>
          ))}
        </Select>
      </Col>
      <Col span={12}>
        <Select
          aria-label="Quarter Year"
          placeholder={<FormattedMessage id="dateRange.year" />}
          value={selectedYear}
          onChange={handleYearChange}
          className={styles.selector}
          size="small"
        >
          {years.map(year => (
            <Option key={year} value={year}>
              {year}
            </Option>
          ))}
        </Select>
      </Col>
    </Row>
  );
}

function YearSelector({ form, onSelectionChange, validRangeYearsBack }) {
  const [selectedYear, setSelectedYear] = useState(null);

  const years = useMemo(
    () => getSelectableYears(validRangeYearsBack).reverse(),
    [validRangeYearsBack]
  );

  useEffect(() => {
    const values = form.getFieldsValue();
    const { dateStart, dateEnd } = values;

    if (dateStart && dateEnd) {
      const startMoment = now(dateStart);
      const endMoment = now(dateEnd);
      const isYearRange =
        startMoment.isSame(endMoment, 'year') &&
        startMoment.isSame(startMoment.clone().startOf('year')) &&
        endMoment.isSame(endMoment.clone().endOf('year'));

      if (isYearRange) {
        setSelectedYear(startMoment.year());
      } else {
        setSelectedYear(null);
      }
    } else {
      setSelectedYear(null);
    }
    // eslint-disable-next-line
  }, [form.getFieldsValue().dateStart, form.getFieldsValue().dateEnd]);

  const handleYearChange = year => {
    setSelectedYear(year);
    if (year !== null) {
      const startDate = now().year(year).startOf('year');
      const endDate = now().year(year).endOf('year');
      form.setFieldsValue({ dateStart: startDate, dateEnd: endDate });
      onSelectionChange?.();
    }
  };

  return (
    <Select
      placeholder={<FormattedMessage id="dateRange.year" />}
      value={selectedYear}
      onChange={handleYearChange}
      size="small"
    >
      {years.map(year => (
        <Option key={year} value={year}>
          {year}
        </Option>
      ))}
    </Select>
  );
}

function Presets({
  form,
  withCustomSelectors,
  extended,
  onDateChange,
  validRangeYearsBack,
}) {
  const { styles } = useDateRangePickerStyles();
  const values = form.getFieldsValue();
  const { dateStart, dateEnd } = values;
  const { presetsOrdered, presetsByKey } = useDateRangePresets({
    extended,
    withCustomSelectors,
  });
  const namedRange = find(toPairs(presetsByKey), ([, val]) =>
    isDateRangeEqual(val, { from: dateStart, to: dateEnd })
  )?.[0];

  const setPresetValue = (name, val) => {
    form.setFieldsValue({
      dateStart: val.from,
      dateEnd: val.to,
    });
    form.validateFields?.();
    onDateChange?.({ dateStart: val.from, dateEnd: val.to });
  };

  const handleSelectionChange = () => {
    form.validateFields?.();
    const currentValues = form.getFieldsValue();
    onDateChange?.(currentValues);
  };

  return (
    <div
      className={classNames(styles.envelope, 'spaces-vert-sm', {
        'with-custom-selectors': withCustomSelectors,
      })}
      onFocus={e => e.stopPropagation()}
    >
      {presetsOrdered.map(({ name, labelId, value: val, isCustomSelector }) => {
        if (isCustomSelector && withCustomSelectors) {
          if (name === 'MONTH') {
            return (
              <MonthYearSelector
                key={name}
                form={form}
                onSelectionChange={handleSelectionChange}
                validRangeYearsBack={validRangeYearsBack}
              />
            );
          }
          if (name === 'QUARTER') {
            return (
              <QuarterYearSelector
                key={name}
                form={form}
                onSelectionChange={handleSelectionChange}
                validRangeYearsBack={validRangeYearsBack}
              />
            );
          }
          if (name === 'YEAR') {
            return (
              <YearSelector
                key={name}
                form={form}
                onSelectionChange={handleSelectionChange}
                validRangeYearsBack={validRangeYearsBack}
              />
            );
          }
        }

        return (
          <DateRangePresetButton
            key={name}
            name={name}
            labelId={labelId}
            value={val}
            setValue={setPresetValue}
            selected={namedRange === name}
          />
        );
      })}
    </div>
  );
}

function DatePickerField({
  fieldName,
  form,
  validRangeYearsBack,
  onDateChange,
}) {
  const values = form.getFieldsValue();
  const value = values[fieldName];

  const handleChange = parsed => {
    const currentValues = form.getFieldsValue();
    const updatedValues = getAdjustedDateRangeValues(
      fieldName,
      parsed,
      currentValues,
      { startField: 'dateStart', endField: 'dateEnd' }
    );
    form.setFieldsValue(updatedValues);
    onDateChange?.(updatedValues);
  };

  return (
    <SingleDateField
      value={value}
      onChange={handleChange}
      validRangeYearsBack={validRangeYearsBack}
    />
  );
}

export function DateRangePicker({
  form,
  withCustomSelectors = false,
  extended = false,
  validRangeYearsBack = 2,
  onDateChange,
}) {
  const { styles: layoutStyles } = useLayoutElementsStyles();
  return (
    <>
      <Row gutter={pxToNumber(cssVariables.spaceNorm2)}>
        <Col span={12} className={layoutStyles.flexCol}>
          <DatePickerField
            fieldName="dateStart"
            form={form}
            validRangeYearsBack={validRangeYearsBack}
            onDateChange={onDateChange}
          />
        </Col>
        <Col span={12} className={layoutStyles.flexCol}>
          <DatePickerField
            fieldName="dateEnd"
            form={form}
            validRangeYearsBack={validRangeYearsBack}
            onDateChange={onDateChange}
          />
        </Col>
      </Row>
      <Presets
        form={form}
        withCustomSelectors={withCustomSelectors}
        extended={extended}
        onDateChange={onDateChange}
        validRangeYearsBack={validRangeYearsBack}
      />
    </>
  );
}
